import { CONTRACT_ADDRESSES } from "@/constants/contract-addresses.constants";

/**
 * Checks if a token contract address is a BLESS token
 * @param contractAddress - The token contract address
 * @returns True if it's a BLESS token
 */
export const isBlessToken = (contractAddress: string): boolean => {
  const lowerAddress = contractAddress.toLowerCase();
  return lowerAddress === CONTRACT_ADDRESSES.BLESS_TOKEN.toLowerCase();
};

/**
 * Generates a deterministic color based on a seed string
 * @param seed - The seed string (usually contract address)
 * @returns HSL color string
 */
const generateDeterministicColor = (seed: string): string => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  const hue = Math.abs(hash % 360);
  const saturation = 45 + (Math.abs(hash) % 30); // 45-75% saturation
  const lightness = 35 + (Math.abs(hash) % 20); // 35-55% lightness

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

/**
 * Gets the background color for a token card based on contract address
 * Handles BLESS tokens with special green color, otherwise generates deterministic color
 * @param contractAddress - The token contract address
 * @param fallbackSeed - Fallback seed if contract address is not available
 * @returns Object with color and whether it's a special case
 */
export const getTokenBackgroundColor = (
  contractAddress?: string,
  fallbackSeed?: string
): {
  backgroundColor: string;
  isSpecialToken: boolean;
  shouldShowImage: boolean;
  imageUrl?: string;
} => {
    // Handle BLESS tokens specially
  if (contractAddress && isBlessToken(contractAddress)) {
    return {
      backgroundColor: "#adf82a", // BLESS green color
      isSpecialToken: true,
      shouldShowImage: false, // Use colored placeholder with text instead of image
    };
  }

  // Generate deterministic color based on contract address or fallback
  const seed = contractAddress || fallbackSeed || "unknown";
  const backgroundColor = generateDeterministicColor(seed);

  return {
    backgroundColor,
    isSpecialToken: false,
    shouldShowImage: false,
  };
};

/**
 * Generates a deterministic gravatar-like SVG pattern for multi-tokens
 * @param tokenIdentifier - The token identifier
 * @param baseColor - The base color from the collection
 * @returns SVG data URL with unique pattern
 */
const generateTokenGravatar = (tokenIdentifier: string, baseColor: string): string => {
  // Generate deterministic values from token identifier
  let hash = 0;
  for (let i = 0; i < tokenIdentifier.length; i++) {
    hash = tokenIdentifier.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Extract HSL components from base color
  const hslMatch = baseColor.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
  const baseHue = hslMatch ? parseInt(hslMatch[1]) : 200;
  const baseSat = hslMatch ? parseInt(hslMatch[2]) : 50;
  const baseLightness = hslMatch ? parseInt(hslMatch[3]) : 45;

  // Generate variations of the base color
  const color1 = `hsl(${baseHue}, ${baseSat}%, ${baseLightness}%)`;
  const color2 = `hsl(${baseHue}, ${Math.min(baseSat + 20, 90)}%, ${Math.max(baseLightness - 15, 20)}%)`;
  const color3 = `hsl(${baseHue}, ${Math.max(baseSat - 10, 30)}%, ${Math.min(baseLightness + 10, 70)}%)`;

  // Generate pattern elements based on hash
  const patternType = Math.abs(hash) % 4;
  const rotation = (Math.abs(hash) % 8) * 45;
  const scale = 0.7 + ((Math.abs(hash) % 6) * 0.1); // 0.7 to 1.2

  let pattern = '';

  switch (patternType) {
    case 0: // Concentric circles
      pattern = `
        <circle cx="16" cy="16" r="12" fill="${color1}"/>
        <circle cx="16" cy="16" r="8" fill="${color2}"/>
        <circle cx="16" cy="16" r="4" fill="${color3}"/>
      `;
      break;
    case 1: // Geometric shapes
      pattern = `
        <rect x="8" y="8" width="16" height="16" fill="${color1}" transform="rotate(${rotation} 16 16)"/>
        <circle cx="16" cy="16" r="6" fill="${color2}"/>
        <polygon points="16,6 26,16 16,26 6,16" fill="${color3}" transform="scale(${scale}) translate(${16*(1-scale)}, ${16*(1-scale)})"/>
      `;
      break;
    case 2: // Triangular pattern
      pattern = `
        <polygon points="16,4 28,28 4,28" fill="${color1}"/>
        <polygon points="16,8 24,24 8,24" fill="${color2}"/>
        <circle cx="16" cy="20" r="4" fill="${color3}"/>
      `;
      break;
    case 3: // Diamond pattern
      pattern = `
        <polygon points="16,2 30,16 16,30 2,16" fill="${color1}"/>
        <polygon points="16,8 24,16 16,24 8,16" fill="${color2}"/>
        <rect x="12" y="12" width="8" height="8" fill="${color3}" transform="rotate(45 16 16)"/>
      `;
      break;
  }

  const svg = `
    <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
      ${pattern}
    </svg>
  `;

  return `data:image/svg+xml;base64,${Buffer.from(svg).toString('base64')}`;
};

/**
 * Gets the background color for multi-token cards including token identifier
 * Handles BLESS tokens with special green color, creates gravatar-like patterns for each token instance within a collection
 * @param contractAddress - The token contract address
 * @param tokenIdentifier - The specific token identifier
 * @param fallbackSeed - Fallback seed if other params not available
 * @returns Color configuration object
 */
export const getMultiTokenBackgroundColor = (
  contractAddress?: string,
  tokenIdentifier?: string,
  fallbackSeed?: string
): {
  backgroundColor: string;
  isSpecialToken: boolean;
  shouldShowImage: boolean;
  imageUrl?: string;
  gravatarUrl?: string;
} => {
  // Handle BLESS tokens specially (even for multi-tokens)
  if (contractAddress && isBlessToken(contractAddress)) {
    return {
      backgroundColor: "#adf82a", // BLESS green color
      isSpecialToken: true,
      shouldShowImage: false, // Use colored placeholder with text instead of image
    };
  }

  // For multi-tokens, use collection color (contract address only) but generate unique pattern
  const collectionSeed = contractAddress || fallbackSeed || "unknown";
  const collectionColor = generateDeterministicColor(collectionSeed);

  // Generate unique gravatar pattern for this token identifier
  const gravatarUrl = tokenIdentifier
    ? generateTokenGravatar(tokenIdentifier, collectionColor)
    : undefined;

  return {
    backgroundColor: collectionColor, // Use collection color as base
    isSpecialToken: false,
    shouldShowImage: !!gravatarUrl, // Show gravatar if we have token identifier
    gravatarUrl, // Custom gravatar pattern
  };
};

/**
 * Legacy function for backwards compatibility - generates simple deterministic color
 * @deprecated Use getTokenBackgroundColor or getMultiTokenBackgroundColor instead
 * @param seed - The seed string
 * @returns HSL color string
 */
export const generateBackgroundColor = (seed: string): string => {
  return generateDeterministicColor(seed);
};
